import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { subscribeToasts, type ToastRecord, type ToastTone } from "@/lib/toast";
import { cn } from "@/lib/cn";
import { X, CircleCheck, CircleAlert, TriangleAlert, Info, type LucideIcon } from "@/icons";

// ToastViewport — mount once at the app root. Subscribes to the UI-agnostic
// lib/toast bus, so any hook/service (e.g. useOrderStatusStream) can emit
// toasts without importing UI. Dedups by dedupKey (replace-in-place), caps the
// stack at 5, auto-dismisses (failures linger longer).
const TONE: Record<ToastTone, { accent: string; Icon: LucideIcon }> = {
  success: { accent: "text-success", Icon: CircleCheck },
  info: { accent: "text-primary", Icon: Info },
  warn: { accent: "text-warning", Icon: TriangleAlert },
  danger: { accent: "text-danger", Icon: CircleAlert },
};

export function ToastViewport() {
  const { t } = useTranslation("ui");
  const [items, setItems] = useState<ToastRecord[]>([]);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const remove = (id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
    const tm = timers.current.get(id);
    if (tm) {
      clearTimeout(tm);
      timers.current.delete(id);
    }
  };

  useEffect(() => {
    const unsub = subscribeToasts((t) => {
      setItems((prev) => {
        const base = t.dedupKey ? prev.filter((p) => p.dedupKey !== t.dedupKey) : prev;
        return [...base, t].slice(-5);
      });
      const ttl = t.ttlMs ?? (t.kind === "danger" || t.kind === "warn" ? 8000 : 5000);
      timers.current.set(
        t.id,
        setTimeout(() => remove(t.id), ttl),
      );
    });
    const map = timers.current;
    return () => {
      unsub();
      map.forEach((tm) => clearTimeout(tm));
      map.clear();
    };
  }, []);

  if (!items.length) return null;

  return (
    <div
      role="region"
      aria-label={t("toast.notifications")}
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:items-end"
    >
      {items.map((item) => {
        const { accent, Icon } = TONE[item.kind];
        return (
          <div
            key={item.id}
            role="status"
            className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border border-border bg-surface p-3.5 shadow-elevated animate-slide-in-bottom sm:animate-fade-in"
          >
            <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", accent)} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-fg">{item.title}</p>
              {item.body && <p className="mt-0.5 break-words text-sm text-fg-muted">{item.body}</p>}
            </div>
            <button
              type="button"
              onClick={() => remove(item.id)}
              aria-label={t("toast.dismiss")}
              className="-m-1 grid h-7 w-7 shrink-0 place-items-center rounded-lg text-fg-subtle transition-colors hover:bg-surface-2 hover:text-fg"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
